import { Fragment } from "react";
import { isOfflineMode } from "utils/checkOfflineMode";
import {
  IconTopSidePreHeat,
  IconBottomSidePreHeat,
  IconBottomSidePyroControl,
  IconTopSidePyroControl,
  IconTopAndBottomSidePreHeat,
  IconRingHeater,
  IconConvectionPreHeat,
  IconAutoPurge,
  IconDropJetFluxer,
  IconAdditionalFluxHead,
  IconOpticalFluxPresenceSensor,
  IconBarcode,
  IconFiducialDynamicCorrection,
  IconPcbWarpageCorrection,
  IconElectromagneticPumpFlow,
  IconDualSolderBaths,
  IconMes,
  IconAutomaticRework,
  IconMachineTemperature,
  IconThermistorFluxSensing,
  IconUltrasonicFluxer,
  IconApNozzle,
  IconJetTip,
  IconJetWave,
  IconMicroNozzle,
  IconWideWave,
  IconCustomNozzle,
  IconThermalCalibration,
  IconNozzleVerification,
  IconAutoNozzleTinning,
  IconAutoLevelDetect,
} from "icons";

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;
const VIDEO_BASE_URL = process.env.REACT_APP_VIDEO_BASE_URL;
const DOCUMENT_BASE_URL = process.env.REACT_APP_DOCUMENT_BASE_URL;

const IS_OFFLINE_MODE = isOfflineMode();

const controlVideo =
  `${VIDEO_BASE_URL}technology/control.mp4` ||
  "assets/videos/control/control.mp4";


// Helper function to get the correct path based on offline mode
const getPath = (onlinePath, offlinePath) => {
  return IS_OFFLINE_MODE ? offlinePath : onlinePath;
};

// Define constants for each image and video path
export const Fusion4Product = getPath(
  `${IMAGE_BASE_URL}products/fusion-4.png`,
  "assets/images/products/fusion-4.png"
);

export const JadeS200MK2Product = getPath(
  `${IMAGE_BASE_URL}products/jade-s-200-mk2.png`,
  "assets/images/products/jade-s-200-mk2.png"
);

export const Pilot = getPath(
  `${IMAGE_BASE_URL}products/pilot.png`,
  "assets/images/products/pilot.png"
);

export const JadeMK2 = getPath(
  `${IMAGE_BASE_URL}products/jade-mk2.png`,
  "assets/images/products/jade-mk2.png"
);

export const QuadronPLC = getPath(
  `${IMAGE_BASE_URL}products/quadron-plc.png`,
  "assets/images/products/quadron-plc.png"
);

export const JadeMK4 = getPath(
  `${IMAGE_BASE_URL}products/jade-mk4.png`,
  "assets/images/products/jade-mk4.png"
);

export const JadePro = getPath(
  `${IMAGE_BASE_URL}products/jade-pro.png`,
  "assets/images/products/jade-pro.png"
);

export const JadeProDex = getPath(
  `${IMAGE_BASE_URL}products/jade-prodex.png`,
  "assets/images/products/jade-prodex.png"
);

// Technology
export const SynchrodexPreHeatLamps = getPath(
  `${IMAGE_BASE_URL}technology/Synchrodex Pro Pre-Heat Lamps.jpg`,
  "assets/images/technology/Synchrodex Pro Pre-Heat Lamps.jpg"
);

export const AutoPurge = getPath(
  `${IMAGE_BASE_URL}technology/fluxing/auto-purge.jpg`,
  "assets/images/technology/fluxing/auto-purge.jpg"
);

export const RingHeater = getPath(
  `${IMAGE_BASE_URL}technology/Ring Heater.jpg`,
  "assets/images/technology/Ring Heater.jpg"
);

// Videos
export const FluxMonitor1 = getPath(
  `${VIDEO_BASE_URL}technology/Drop-Jet Fluxing.mp4`,
  "assets/videos/technology/Drop-Jet Fluxing.mp4"
);

export const BottomSidePreHeat = getPath(
  `${VIDEO_BASE_URL}technology/Bottom Side Pre-Heat.MP4`,
  "assets/videos/technology/Bottom Side Pre-Heat.MP4"
);

export const DualFluxer = getPath(
  `${VIDEO_BASE_URL}technology/Dual Fluxer.mp4`,
  "assets/videos/technology/Dual Fluxer.mp4"
);

export const ThermistorFluxSensingVideo = getPath(
  `${VIDEO_BASE_URL}technology/Thermistor Flux Presence Sensor.mp4`,
  "assets/videos/technology/Thermistor Flux Presence Sensor.mp4"
);

export const MicroNozzleVideo = getPath(
  `${VIDEO_BASE_URL}technology/Micro Nozzle.mp4`,
  "assets/videos/technology/Micro Nozzle.mp4"
);

export const ThermalNozzleVideo = getPath(
  `${VIDEO_BASE_URL}technology/Thermal Nozzle Calibration.mp4`,
  "assets/videos/technology/Thermal Nozzle Calibration.mp4"
);

export const NozzleVerificationVideo = getPath(
  `${VIDEO_BASE_URL}technology/Nozzle Verification.mp4`,
  "assets/videos/technology/Nozzle Verification.mp4"
);

export const TwinBathVideo = getPath(
  `${VIDEO_BASE_URL}technology/Top and Bottom Side Pre-Heat.mp4`,
  "assets/videos/technology/Top and Bottom Side Pre-Heat.mp4"
);

export const TopSidePreHeat = getPath(
  `${VIDEO_BASE_URL}technology/Preheater script Example.mp4`,
  "assets/videos/technology/Preheater script Example.mp4"
);

export const TopAndBottomPreHeat = getPath(
  `${VIDEO_BASE_URL}technology/Top and Bottom Side Pre-Heat.mp4`,
  "assets/videos/technology/Top and Bottom Side Pre-Heat.mp4"
);

export const ConvectionPreHeat = getPath(
  `${VIDEO_BASE_URL}technology/Stacker Convection Heater Animation.mp4`,
  "assets/videos/technology/Stacker Convection Heater Animation.mp4"
);

export const CLBottomSidePyroControlVideo = getPath(
  `${VIDEO_BASE_URL}technology/Closed-Loop Bottom Side Pyro Control.mp4`,
  "assets/videos/technology/Closed-Loop Bottom Side Pyro Control.mp4"
);

export const CLTopSidePyroControlVideo = getPath(
  `${VIDEO_BASE_URL}technology/Closed-Loop Top Side Pyro Control.mp4`,
  "assets/videos/technology/Closed-Loop Top Side Pyro Control.mp4"
);

export const UltrasonicFluxing = getPath(
  `${VIDEO_BASE_URL}technology/Ultrasonic Fluxing.mp4`,
  "assets/videos/technology/Ultrasonic Fluxing.mp4"
);

const fluxingSliders = [
  {
    type: "image",
    name: "Auto-Flux Purge",
    option: "auto_purge",
    icon: IconAutoPurge,
    description: "Ensure your production line runs smoothly with the Auto-Flux Purge system. It efficiently removes residual flux, minimizing downtime and enhancing product quality. Designed for precision and reliability, it's the ideal solution for maintaining cleanliness and consistency in your soldering processes. ",
    content: AutoPurge,
  },
  {
    type: "video",
    name: "Drop-Jet Fluxer",
    option: "drop_jet_fluxing",
    icon: IconDropJetFluxer,
    description: "Achieve precise flux application with the Drop-Jet Fluxer. This state-of-the-art device delivers accurate, controlled flux drops, ensuring optimal solder joints and reducing waste. Perfect for high-demand environments, the Drop-Jet Fluxer enhances efficiency and consistency in every project.",
    content: FluxMonitor1,
  },
  {
    type: "video",
    name: "Additional Flux Head",
    option: "dual_fluxer",
    icon: IconAdditionalFluxHead,
    description: "Maximize productivity with the Additional Flux head, designed to handle two different flux types seamlessly. Switch between applications with ease, ensuring versatility and efficiency in your soldering processes. Engineered for flexibility and reliability, the Additional Flux Head is the perfect addition to your production line. ",
    content: DualFluxer,
  },
  {
    type: "video",
    name: "Optical Flux Presence Sensor",
    option: "optical_fluc_presence_sensor",
    icon: IconOpticalFluxPresenceSensor,
    description:
      "Ensure perfect flux application with the Optical Flux Presence Sensor. This advanced sensor detects the precise presence of flux, guaranteeing optimal soldering conditions. Enhance quality control and reduce errors with real-time monitoring.",
    content: `${VIDEO_BASE_URL}enhancements/Optical Flux Presence Sensor.mp4`,
    enhancements: true,
  },
  {
    type: "video",
    name: "Thermistor Flux Sensing",
    option: "thermistor_flux_sensing",
    icon: IconThermistorFluxSensing,
    description: "Optimize your flux application with Thermistor Flux Sensing technology. This innovative solution provides precise temperature monitoring, ensuring perfect flux conditions for superior solder joints. Enhance your production line's efficiency and product quality with reliable thermistor-based sensing.",
    content: ThermistorFluxSensingVideo,
  },
  {
    type: "video",
    name: "Ultrasonic Fluxer",
    option: "ultrasonic_fluxing",
    icon: IconUltrasonicFluxer,
    description:
      "Revolutionize your soldering process with the Ultrasonic Fluxer. Utilizing ultrasonic waves for superior flux distribution, it ensures consistent and precise application. Reduce waste and improve joint quality with this advanced, efficient fluxing solution. ",
    content: UltrasonicFluxing,
    enhancements: true,
  },
];

const preheatingSliders = [
  {
    type: "video",
    name: "Top Side Pre-Heat",
    option: "top_side_pre_heat",
    icon: IconTopSidePreHeat,
    description: "Optimize your soldering process with our Top Side Pre-Heat system. Ensure consistent heat distribution, reduce thermal stress, and improve solder joint reliability. Perfect for complex assemblies, this system enhances your production efficiency while maintaining top-quality results. Invest in precision heating for superior electronic manufacturing.",
    content: TopSidePreHeat,
  },
  {
    type: "video",
    name: "Bottom Side Pre-Heat",
    option: "bottom_side_pre_heat",
    icon: IconBottomSidePreHeat,
    description:
      "Achieve flawless soldering with our Bottom Side Pre-Heat technology. Designed to evenly heat the underside of your PCB, it minimizes warping and thermal shock, resulting in stronger, more reliable joints. Enhance your manufacturing process with this essential tool for quality and consistency.",
    content: BottomSidePreHeat,
  },
  {
    type: "video",
    name: "Closed-Loop Bottom Side Pyro Control",
    option: "bottom_side_pyro_control",
    icon: IconBottomSidePyroControl,
    description:
      "Experience unparalleled temperature accuracy with our Closed-Loop Bottom Side Pyrometer Control. This advanced system continuously monitors and adjusts heat levels, ensuring optimal thermal conditions for soldering. Eliminate guesswork and achieve perfect results every time with our state-of-the-art pyrometer technology.",
    content: CLBottomSidePyroControlVideo,
  },
  {
    type: "video",
    name: "Closed-Loop Top Side Pyro Control",
    option: "closed_loop_control",
    icon: IconTopSidePyroControl,
    description:
      "Ensure precision and consistency with our Closed-Loop Top Side Pyrometer Control. This system provides real-time temperature adjustments, maintaining ideal conditions for soldering. Achieve superior quality and reliability in your electronic assemblies with our cutting-edge pyrometer control technology.",
    content: CLTopSidePyroControlVideo,
  },
  {
    type: "video",
    name: "Top and Bottom Side Pre-Heat",
    option: "top_and_bottom_side_pre_heat",
    icon: IconTopAndBottomSidePreHeat,
    description: "Maximize your soldering efficiency with Top and Bottom Side Pre-Heat. This dual heating system ensures uniform temperature distribution, reducing thermal stress and enhancing solder joint integrity. Ideal for complex PCB assemblies, it delivers consistent, high-quality results for your manufacturing needs.",
    content: TopAndBottomPreHeat,
  },
  {
    type: "image",
    name: "Ring Heater",
    option: "ring_heater",
    icon: IconRingHeater,
    description:
      "Upgrade your soldering setup with our versatile Ring Heater.  Designed for even heat distribution around the soldering area, it ensures uniform temperature control, reducing thermal gradients. Perfect for precision soldering tasks, this heater enhances reliability and quality in electronic manufacturing.",
    content: RingHeater,
  },
  {
    type: "video",
    name: "Convection Pre-Heat",
    option: "convection_pre_heat",
    icon: IconConvectionPreHeat,
    description: "Boost your soldering performance with Convection Pre-Heat. This system uses controlled airflow to uniformly heat PCBs, minimizing thermal stress and improving solder joint quality. Ideal for complex assemblies, it offers efficient, consistent pre-heating for superior manufacturing outcomes.",
    content: ConvectionPreHeat,
  },
];

const solderingSliders = [
  {
    type: "video",
    name: "AP-1 Nozzles",
    option: "ap_nozzle",
    icon: IconApNozzle,
    content: getPath(
      `${VIDEO_BASE_URL}technology/AP Nozzle.mp4`,
      "assets/videos/technology/AP Nozzle.mp4"
    ),
    description:
      "AP-1 nozzles are the most flexible wetted nozzles ranging from 2.5–16 mm. They come in extended lengths, work equally in all directions, and have excellent debridging properties. These nozzles are the staple of the modern selective soldering process.",
  },
  {
    type: "video",
    name: "Jet-Tips",
    option: "jet_tip",
    icon: IconJetTip,
    content: getPath(
      `${VIDEO_BASE_URL}technology/Jet Tip.mp4`,
      "assets/videos/technology/Jet Tip.mp4"
    ),
    description: "These adaptations of the flexible AP-1 nozzles are available in a wide range of sizes expanding up to 45 mm in diameter. They are identified by their high, stable flow characteristics, allowing higher thermal transfer and adaptation to long lead lengths. These nozzles can operate at two different contact diameters when programmed with different dip and vector heights. The debridging capability is less than that of the AP-1 nozzles. They are also available in extended lengths for increased clearance.",
  },
  {
    type: "video",
    name: "Jetwave",
    option: "jet_wave",
    icon: IconJetWave,
    description:
      "Ensure optimal process control with Flux Flow Monitoring. It detects flow and lack of flow, estimates spray volume, and halts the process if spraying stops, guaranteeing high-level precision and reliability.",
    content: getPath(
      `${VIDEO_BASE_URL}enhancements/Jet Wave.mp4`,
      "assets/videos/enhancements/Jet Wave.mp4"
    ),
    enhancements: true,
  },
  {
    type: "video",
    name: "Micro Thermal Nozzle",
    option: "1.2mm_micro_nozzle",
    icon: IconMicroNozzle,
    description:
      "Introducing the 1.2 Micro Thermal Nozzle: a multi-patented marvel with the world's smallest keepout zones. Experience stable flow without freezing, setting new standards in precision and efficiency.",
    content: MicroNozzleVideo,
  },
  {
    type: "video",
    name: "Wide Wave Nozzle",
    option: "wide_wave",
    icon: IconWideWave,
    content: getPath(
      `${VIDEO_BASE_URL}technology/Wide Wave.mp4`,
      "assets/videos/technology/Wide Wave.mp4"
    ),
    description: "The Wide Wave Nozzle is a fully inerted wide nozzle in the 75 to 150 mm width range. It excels at soldering single sided PCBs that were once part of a legacy wave process but now only form a portion of current selective production.",
  },
  {
    type: "video",
    name: "Custom Nozzle",
    option: "custom_nozzle",
    icon: IconCustomNozzle,
    content: getPath(
      `${VIDEO_BASE_URL}technology/Custom Multi Dip Nozzle.mp4`,
      "assets/videos/technology/Custom Multi Dip Nozzle.mp4"
    ),
    description: "Pillarhouse has over 40 years of experience and numerous patents in the realm of custom or multi-dip nozzles. These nozzles are ideal for high-throughput, low-flexibility environments where throughput is the primary metric. The inertion technology reduces general dross production while stabilizing the top flow contact surface.",
  },
  {
    type: "video",
    name: "Thermal Calibration",
    option: "thermal_calibration",
    icon: IconThermalCalibration,
    description:
      "Achieve unmatched accuracy with Pillarhouse's Thermal Nozzle Calibration. Our integrated system measures the true contact patch of solder, ensuring unparalleled location precision. Unlike competitors, we rely on actual solder flow rather than known mechanical positions, setting us apart in delivering precise results.",
    content: ThermalNozzleVideo,
  },
  {
    type: "video",
    name: "Nozzle Verification",
    option: "nozzle_verfication",
    icon: IconNozzleVerification,
    description:
      "Ensure precision with our Nozzle Verification system. Using a camera-based system, it verifies the installed nozzle in each bath, preventing incorrect soldering and potential PCB damage. Programs cannot run without the correct nozzle, which is locked in with a picture to assist operators.",
    content: NozzleVerificationVideo,
  },
  {
    type: "video",
    name: "Auto Nozzle Tinning",
    option: "auto_nozzle_tinning",
    icon: IconAutoNozzleTinning,
    description:
      "Enhance nozzle performance with Pillarhouse's Automatic Nozzle Tinning. This hands-free process applies just the right amount of cleaning medium, ensuring optimal nozzle wetting and reliability for peak functionality.",
    content: `${VIDEO_BASE_URL}enhancements/Auto Nozzle Conditioning.mp4`,
    enhancements: true,
  },
  {
    type: "video",
    name: "Auto Level Detect and Top Up",
    option: "auto_level_detect",
    icon: IconAutoLevelDetect,
    description:
      "Maintain perfect solder levels with Auto Level Detect and Top Up. This feature closely monitors solder levels to ensure constant head pressure and precise control of solder pumping height. Small incremental loading and level anticipation reduce cycle times for enhanced efficiency.",
    content: `${VIDEO_BASE_URL}enhancements/Feeder short.mp4`,
    enhancements: true,
  },
];

// Products in options

export const EXISTING_KEYS = {
  Synchrodex: "Synchrodex",
  "Orissa Synchrodex": "Orissa Synchrodex",
  "Orissa Synchrodex Pro": "Orissa Synchrodex Pro",
  "Fusion Layouts": "Fusion Layouts",
};

export const EXISTING_TECHNOLOGY_KEYS = {
  fluxing: "fluxing",
  preheating: "preheating",
  soldering: "soldering",
  other: "other",
  auto_purge: 'auto_purge',
  djf: 'djf',
  dual_fluxer: 'dual_fluxer',
  ofps: 'ofps',
  thermistor_flux_sensing: 'thermistor_flux_sensing',
  usf: 'usf',
  bottom_side_pre_heat: 'bottom_side_pre_heat',
  closed_loop_bottom_side_pyro_control: 'closed_loop_bottom_side_pyro_control',
  closed_loop_top_side_pyro_control: 'closed_loop_top_side_pyro_control',
  convection_pre_heat: 'convection_pre_heat',
  ring_heater: 'ring_heater',
  top_and_bottom_side_pre_heat: 'top_and_bottom_side_pre_heat',
  top_side_pre_heat: 'top_side_pre_heat',
  ap_nozzle: 'ap_nozzle',
  custom_nozzle: 'custom_nozzle',
  jet_wave: 'jet_wave',
  jet_tip_nozzle: 'jet_tip_nozzle',
  micro_nozzle: 'micro_nozzle',
  wide_wave: 'wide_wave',
  control: 'control',
  offline: 'offline'
};

// Slide `option` for technologies whose key differs from it
export const TECHNOLOGY_SLIDE_OPTIONS = {
  djf: 'drop_jet_fluxing',
  ofps: 'optical_fluc_presence_sensor',
  usf: 'ultrasonic_fluxing',
  closed_loop_bottom_side_pyro_control: 'bottom_side_pyro_control',
  closed_loop_top_side_pyro_control: 'closed_loop_control',
  jet_tip_nozzle: 'jet_tip',
  micro_nozzle: '1.2mm_micro_nozzle',
  control: 'control_software',
};

export const TECHNOLOGY_LINKS = [
  {
    name: "fluxing",
    key: "fluxing",
    items: [
      { isDisabled: false, key: 'auto_purge' },
      { isDisabled: false, key: 'djf' },
      { isDisabled: false, key: 'dual_fluxer', option: '' },
      { isDisabled: false, key: 'ofps', option: '' },
      { isDisabled: false, key: 'thermistor_flux_sensing', option: '' },
      { isDisabled: false, key: 'usf', option: '' },
    ],
  },
  {
    name: "preheating",
    key: "preheating",
    items: [
      { isDisabled: false, key: 'bottom_side_pre_heat', option: ''  },
      { isDisabled: false, key: 'closed_loop_bottom_side_pyro_control', option: '' },
      { isDisabled: false, key: 'closed_loop_top_side_pyro_control', option: '' },
      { isDisabled: false, key: 'convection_pre_heat', option: "" },
      { isDisabled: false, key: 'ring_heater', option: "" },
      { isDisabled: false, key: 'top_and_bottom_side_pre_heat', option: "" },
      { isDisabled: false, key: 'top_side_pre_heat', option: '' },
    ],
  },
  {
    name: "soldering",
    key: "soldering",
    items: [
      { isDisabled: false, key: 'ap_nozzle', option: '' },
      { isDisabled: false, key: 'custom_nozzle', option: '' },
      { isDisabled: false, key: 'jet_wave', option: '' },
      { isDisabled: false, key: 'jet_tip_nozzle', option: '' },
      { isDisabled: false, key: 'micro_nozzle', option: '' },
      { isDisabled: false, key: 'wide_wave', option: '' },
    ],
  },
  {
    name: "software",
    key: "software",
    items: [
      { isDisabled: false, key: 'control'},
      { isDisabled: false, key: 'offline'},
      { isDisabled: true, title: 'inspection'}
    ]
  },
  {
    name: "other",
    key: "other",
    items: [
      { isDisabled: false, key: 'other', option: 'fiducial_dynamic_correction', title: 'fiducial_dynamic_correction'},
      { isDisabled: false, key: 'other', option: 'pcb_warpage_correction', title: 'pcb_warpage_correction'},
      { isDisabled: false, key: 'other', option: 'electromagnetic_pump', title: 'electromagnetic_pump' },
      { isDisabled: false, key: 'other', option: 'dual_solder_baths', title: 'dual_solder_baths' },
      { isDisabled: false, key: 'other', option: 'manufacturing_equipment_systems', title: 'manufacturing_equipment_systems' },
      { isDisabled: false, key: 'other', option: 'automatic_rework_integration', title: 'automatic_rework_integration' },
      { isDisabled: false, key: 'other', option: 'internal_monitoring_temperature', title: 'internal_monitoring_temperature' },
      { isDisabled: false, key: 'other', option: 'automatic_program_id', title: 'automatic_program_id' },
    ],
  },
];

export const FEATURES = {
  default: [
    {
      xPos: 262,
      yPos: 85,
      text: "IPC-2591 (CFX), Smart Factory ready",
    },
    {
      xPos: 189,
      yPos: 212,
      text: "Colour programming camera",
    },
    {
      xPos: 261,
      yPos: 330,
      text: "Drop Jet fluxer",
    },
    {
      xPos: 160,
      yPos: 454,
      text: "Flux level sensor",
    },
    {
      xPos: 305,
      yPos: 537,
      text: "Multiple level password protection",
    },
    {
      xPos: "30%",
      yPos: 627,
      text: "508mm x 508mm (20' x 20') and 610mm x 610mm (24' x 24') board handling sizes",
    },
    {
      xPos: 901,
      yPos: 512,
      text: "SMEMA compatible",
    },
    {
      xPos: 958,
      yPos: 396,
      text: "New PillarCOMM control software",
    },
    {
      xPos: 920,
      yPos: 250,
      text: "Internal fume extraction",
    },
    {
      xPos: 1075,
      yPos: 165,
      text: "Day-to-day service kit",
    },
    {
      xPos: 964,
      yPos: 80,
      text: "DC drives on all axes",
    },
  ],
  "Orissa Synchrodex": [
    {
      xPos: 162,
      yPos: 85,
      text: "In-line motor driven auto width adjust through feed synchronous movement conveyor",
    },
    {
      xPos: 189,
      yPos: 212,
      text: "Conveyor side clamping",
    },
    {
      xPos: 201,
      yPos: 330,
      text: "Integral PC and machine mounted monitor",
    },
    {
      xPos: 160,
      yPos: 454,
      text: "Motorised auto-solder top-up (wire feed) & solder level detect",
    },
    {
      xPos: 305,
      yPos: 537,
      text: "Heated inerted Nitrogen system",
    },
    {
      xPos: "30%",
      yPos: 637,
      text: "Titanium Drop-Jet fluxer",
    },
    {
      xPos: 901,
      yPos: 512,
      text: "Set of AP solder nozzle tips",
    },
    {
      xPos: 958,
      yPos: 396,
      text: "Internal fume extraction",
    },
    {
      xPos: 920,
      yPos: 250,
      text: "Colour programming camera",
    },
    {
      xPos: 1075,
      yPos: 185,
      text: "Solder wave height measurement and correction",
    },
    {
      xPos: 964,
      yPos: 80,
      text: "Pump rpm",
    },
    {
      xPos: 62,
      yPos: 15,
      text: "Auto-nozzle conditioning system",
    },
    {
      xPos: 139,
      yPos: 142,
      text: "Thermal nozzle calibration system using integrated setting camera",
    },
    {
      xPos: 111,
      yPos: 270,
      text: "Windows® based PillarCOMM ‘Point & Click’ interface",
    },
    {
      xPos: 130,
      yPos: 404,
      text: "Light stack",
    },
    {
      xPos: 80,
      yPos: 587,
      text: "Auto fiducial recognition and correction system",
    },
    {
      xPos: 80,
      yPos: 647,
      text: "Flux level sensor",
    },
    {
      xPos: 901,
      yPos: 602,
      text: "Multiple level password protection",
    },
    {
      xPos: 988,
      yPos: 456,
      text: "PillarPAD offline programming system",
    },
    {
      xPos: 990,
      yPos: 320,
      text: "SMEMA compatible",
    },
    {
      xPos: 775,
      yPos: 135,
      text: "Process viewing camera with record feature",
    },
    {
      xPos: 1164,
      yPos: 80,
      text: "Lead-free capability",
    },
    {
      xPos: 964,
      yPos: 15,
      text: "Day-to-day service kit",
    },
  ],
  "Orissa Synchrodex Pro": [
    {
      xPos: 162,
      yPos: 85,
      text: "508mm x 508mm (20' x 20') and 610mm x 610mm (24' x 24') board handling sizes",
    },
    {
      xPos: 189,
      yPos: 212,
      text: "Interchangeable Mechanical or Magnetic solder bath and pump",
    },
    {
      xPos: 201,
      yPos: 330,
      text: "Lead-free solder capability",
    },
    {
      xPos: 160,
      yPos: 454,
      text: "Inerted Nitrogen system",
    },
    {
      xPos: 305,
      yPos: 537,
      text: "Set of AP style solder nozzle tips",
    },
    {
      xPos: "30%",
      yPos: 637,
      text: "Automatic nozzle conditioning system",
    },
    {
      xPos: 901,
      yPos: 512,
      text: "Thermal nozzle calibration",
    },
    {
      xPos: 958,
      yPos: 396,
      text: "Solder level detect and motorised wire feeder",
    },
    {
      xPos: 920,
      yPos: 250,
      text: "Wave height correction",
    },
    {
      xPos: 1075,
      yPos: 185,
      text: "DC drives on all axes",
    },
    {
      xPos: 964,
      yPos: 80,
      text: "Colour programming camera",
    },
    {
      xPos: 62,
      yPos: 15,
      text: "Automatic fiducial correction",
    },
    {
      xPos: 139,
      yPos: 142,
      text: "Drop Jet fluxer",
    },
    {
      xPos: 111,
      yPos: 270,
      text: "Internal fume extraction",
    },
    {
      xPos: 130,
      yPos: 404,
      text: "Light stack",
    },
    {
      xPos: 80,
      yPos: 587,
      text: "Integral PC and monitor",
    },
    {
      xPos: 80,
      yPos: 647,
      text: "New PillarCOMM control software",
    },
    {
      xPos: 901,
      yPos: 602,
      text: "New PillarCOMM control software",
    },
    {
      xPos: 988,
      yPos: 456,
      text: "Multiple level password protection",
    },
    {
      xPos: 990,
      yPos: 320,
      text: "IPC-2591 (CFX), Smart Factory ready",
    },
  ],
  "Fusion Layouts": [
    {
      xPos: 162,
      yPos: 85,
      text: "Integral PC and machine mounted Titanium monitor",
    },
    {
      xPos: 189,
      yPos: 212,
      text: "In-line motor driven width adjust through feed conveyor",
    },
    {
      xPos: 201,
      yPos: 330,
      text: "Conveyor side clamping",
    },
    {
      xPos: 160,
      yPos: 454,
      text: "DC servo drives with encoders on X, Y & Z axis",
    },
    {
      xPos: 305,
      yPos: 537,
      text: "Inerted Nitrogen system",
    },
    {
      xPos: "30%",
      yPos: 637,
      text: "Internal fume extraction",
    },
    {
      xPos: 901,
      yPos: 512,
      text: "Titanium Drop-Jet fluxer",
    },
    {
      xPos: 958,
      yPos: 396,
      text: "Motorised wire feed auto solder top-up & solder level detect",
    },
    {
      xPos: 920,
      yPos: 250,
      text: "Solder wave height measurement and correction system",
    },
    {
      xPos: 1075,
      yPos: 185,
      text: "Solder bath coding – identifies correct bath for program",
    },
    {
      xPos: 964,
      yPos: 80,
      text: "Thermal nozzle calibration system using integrated setting camera",
    },
    {
      xPos: 62,
      yPos: 15,
      text: "Auto-nozzle conditioning system",
    },
    {
      xPos: 139,
      yPos: 142,
      text: "Pump rpm",
    },
    {
      xPos: 111,
      yPos: 270,
      text: "Set of AP solder nozzle tips",
    },
    {
      xPos: 130,
      yPos: 404,
      text: "Colour programming camera",
    },
    {
      xPos: 80,
      yPos: 587,
      text: "Process viewing camera",
    },
    {
      xPos: 80,
      yPos: 647,
      text: "Automatic fiducial correction system",
    },
    {
      xPos: 901,
      yPos: 602,
      text: "Multiple level password protection",
    },
    {
      xPos: 988,
      yPos: 456,
      text: "Light stack",
    },
    {
      xPos: 990,
      yPos: 320,
      text: "Flux level sensor",
    },
    {
      xPos: 901,
      yPos: 602,
      text: "PillarCOMM Windows® based ‘Point & Click’ interface",
    },
    {
      xPos: 988,
      yPos: 456,
      text: "Lead-free compatible",
    },
    {
      xPos: 990,
      yPos: 320,
      text: "Day-to-day service kit",
    },
  ],
};

const SOFTWARE_VARIATIONS = [
  {
    variant: "PillarCOMM",
    machines: [
      { name: "Jade Pro", url: "https://www.pillarhouse.co.uk/product/jade-pro/" },
      { name: "Jade Prodex", url: "https://www.pillarhouse.co.uk/product/jade-prodex/" },
    ],
  },
  {
    variant: "PillarCOMM.NET",
    machines: [
      { name: "Fusion", url: "https://www.pillarhouse.co.uk/product/orissa-fusion/" },
      { name: "Synchrodex Pro", url: "https://www.pillarhouse.co.uk/product/orissa-synchrodex-pro/" },
    ],
  },
  {
    variant: "PillarCOMMX",
    machines: [
      { name: "Synchrodex", url: "https://www.pillarhouse.co.uk/product/orissa-synchrodex/" },
      { name: "Synchrodex Combo", url: "https://www.pillarhouse.co.uk/product/orissa-synchrodex-combo/" },
      { name: "Jade MKII", url: "https://www.pillarhouse.co.uk/product/jade-mk-ii/" },
      { name: "Jade MKIV", url: "https://www.pillarhouse.co.uk/product/jade-mk-iv/" },
      { name: "Jade Handex", url: "https://www.pillarhouse.co.uk/product/jade-handex/" },
    ],
  },
  {
    variant: "PillarCOMMXLITE",
    machines: [
      { name: "Pilot", url: "https://www.pillarhouse.co.uk/product/pilot/" },
    ],
  },
];

export const FEATURES_INFOS = {
  fluxing: [
    "Drop Jet Flux Heads – Available with multiple interchangeable orifice sizes, enabling a wide range of deposition volumes while accommodating different viscosities and chemistries.",
    "Ultrasonic Flux Heads – Offered in standard and wide-horn versions, allowing for broader dispersion patterns while maintaining control over overspray and volume.",
    "Multi-Head Configurations – Supports dual-chemistry applications with completely separated bottle systems, enhancing flexibility and precision.",
    "Dedicated Purge Locations – Collects overspray efficiently, with automated purging for a clean and consistent process.",
    "Fully Automated Purge & Replenish Systems – Prevents flux from passing through valves, allowing the use of RMA fluxes and other high-solids or high-acid number chemistries without clogging or contamination.",
  ],
  auto_purge: [
    "Automated Cleaning Cycle: Prevents nozzle blockages and ensures continuous, uniform flux application.",
    "Consistent Spray Performance: Maintains accurate flux deposition for improved solder joint quality.",
    "Reduced Maintenance Downtime: Minimizes manual intervention and enhances process efficiency.",
    "Extended Equipment Lifespan: Prevents dried flux buildup, reducing wear on system components.",
    "Customizable Purge Intervals: Adjustable settings to match process requirements and flux type."
  ],
  djf: [
    "High Precision Dispensing: Ensures consistent flux application for reliable solder joints.",
    "Non-Contact Operation: Prevents mechanical damage to delicate PCB components.",
    "Adjustable Flow Control: Allows fine-tuning of flux volume to match specific soldering requirements.",
    "Reduced Flux Consumption: Minimizes waste, optimizing process efficiency and cost-effectiveness.",
    "Low Maintenance Design: Features self-cleaning capabilities to prevent clogging and ensure long-term performance."
  ],
  dual_fluxer: [
    "Dual Flux Compatibility: Supports two different flux types or viscosities for enhanced process flexibility.",
    "Automatic Flux Switching: Enables seamless transitions between fluxes, eliminating downtime and manual adjustments.",
    "Precision Jet or Spray Application: Ensures accurate and controlled flux deposition for consistent soldering results.",
    "Reduced Cross-Contamination: Separate fluxing nozzles and dedicated fluid lines maintain purity and reliability.",
    "Increased Process Efficiency: Reduces the need for multiple soldering setups, saving time and production costs.",
  ],
  thermistor_flux_sensing: [
    "Real-Time Flux Monitoring: Detects flux presence pre- flux process for reliable application control.",
    "Prevents Flux-Related Defects: Ensures flux is applied, reducing issues like poor wetting or cold solder joints.",
    "Automatic Process Adjustments: Can trigger alerts or corrective actions when flux levels are too low or inconsistent.",
    "Non-Intrusive Sensing: Monitors flux flow without disrupting the selective soldering process.",
    "Enhanced Process Stability: Reduces variability in flux application, ensuring smooth production operation.",
  ],
  usf: [
    "Fine Mist Application: Ultrasonic vibrations break flux into micro-droplets for even coverage and improved solderability.",
    "Highly Controlled Deposition: Delivers precise flux amounts, reducing excess residue and minimizing cleanup.",
    "Non-Contact Technology: Prevents mechanical wear and contamination, extending equipment lifespan.",
    "Lower Flux Consumption: Enhances efficiency by optimizing flux usage, reducing overall costs.",
    "Improved Process Consistency: Ensures repeatable, high-quality soldering results with minimal variation."
  ],
  soldering: [
    "JetWave Technology – High-flow JetWave nozzles are available in sizes from 6mm to 60mm. Originally developed for MIL/AERO edge connectors, they have proven ideal for high-mass, high-pin-count connectors due to superior thermal transfer characteristics and accommodation for longer lead protrusions.",
    "AP-1 Wetted Nozzle Tip – A standout performer among wetted nozzles, offering a high range of versatility, excellent debridging performance, and lower cost of ownership compared to competitive options.",
    "Jet_Tip Nozzle – Bridges the gap between AP-1 and JetWave, providing higher flow than AP-1 with full control and enhanced heat transfer. While it sacrifices some debridging performance, it offers flexibility by allowing two different contact sizes in the same nozzle.",
    "Micro Thermal Nozzle – The result of over 20 years of development, the Micro Thermal 1.5mm Nozzle revolutionizes micro soldering. Unlike traditional micro nozzles (under 2.5mm-3mm) that suffer from minimal heat transfer and rapid dewetting, our patented MultiFlow design and advanced surface features allow the Micro Thermal 1.5mm Nozzle to perform like a 4mm nozzle while reaching into spaces with as little as 0.0015” pad spacing (dependent on component height).",
  ],
  preheating: [
    "Topside Closed-Loop IR Heating – Standard on all machines, ensuring the PCB reaches and maintains optimal temperature throughout the cycle. Zones can be activated or deactivated based on product requirements, with intensity adjustable per zone for maximum control.",
    "Bottomside IR Preheat – Complements topside IR heating by accelerating the process from below, ensuring homogeneous heating of internal PCB layers.",
    "Full Convection Oven – Unlike standard hot air blowers, our system leverages edge-loss characteristics to refresh energy around the entire unit, whether a single PCB or a fully housed module. It is unaffected by reflective materials such as plastic and aluminum, working seamlessly with IR systems for rapid, uniform heating.",
    "Stacker Convection Oven – Delivers the benefits of convection heating without extended preheat times. Designed for high-thermal-demand products requiring 6-8 minutes to target temperature, this system achieves an output cycle time of just 20-30 seconds—unmatched by any competitor in the market.",
  ],
  bottom_side_pre_heat: [
    "Even Heat Distribution: Infrared technology provides consistent and uniform heating across the PCB.",
    "Improved Solder Joint Quality: Reduces thermal shock and ensures better solder flow and wetting.",
    "Adjustable Temperature Control: Customizable heating profiles for different board materials and thicknesses.",
    "Enhanced Process Stability: Minimizes defects such as cold solder joints and warpage.",
    "Energy-Efficient Operation: Focused heating reduces power consumption and heat loss.",
  ],
  closed_loop_bottom_side_pyro_control: [
    "Real-Time Temperature Monitoring: Continuously measures bottom-side PCB temperatures for precise process control.",
    "Closed-Loop Feedback System: Automatically adjusts heating parameters to maintain consistent temperatures.",
    "Prevents Overheating and Cold Solder Joints: Ensures optimal pre-heating, reducing defects caused by thermal stress.",
    "Non-Contact Infrared Sensing: Provides accurate readings without interfering with the soldering process.",
    "Enhanced Process Stability: Improves repeatability and reliability for high-quality solder joints.",
],
  closed_loop_top_side_pyro_control: [
    "Real-Time Temperature Monitoring: Continuously measures top side PCB temperatures for precise process control.",
    "Closed-Loop Feedback System: Automatically adjusts heating parameters to maintain consistent temperatures.",
    "Prevents Overheating and Cold Solder Joints: Ensures optimal pre-heating, reducing defects caused by thermal stress.",
    "Non-Contact Infrared Sensing: Provides accurate readings without interfering with the soldering process.",
    "Enhanced Process Stability: Improves repeatability and reliability for high-quality solder joints.",
],
  convection_pre_heat: [
    "Uniform Heat Distribution: Hot air convection evenly warms the PCB, preventing localized overheating.",
    "Reduced Thermal Shock: Gradual heating minimizes stress on components and board materials.",
    "Enhanced Soldering Performance: Improves flux activation and solder wetting for high-quality joints.",
    "Adjustable Temperature Profiles: Customizable heating settings to accommodate different board designs and materials.",
    "Energy-Efficient and Controlled Heating: Provides stable, repeatable heating with minimal heat loss.",
],
  ring_heater: [
    "Focused Infrared Heating: Provides targeted, precise heating to specific areas of the PCB for controlled pre-soldering temperatures.",
    "Uniform Heat Distribution: The ring design ensures even temperature across the heating zone, improving solder flow and joint consistency.",
    "Reduced Thermal Stress: Minimizes thermal shock by gently warming the board, protecting sensitive components.",
    "Energy-Efficient: Infrared radiation heats only the necessary areas, reducing energy consumption and heat loss.",
    "Adjustable Temperature Control: Customizable heat settings to suit various PCB types and soldering requirements.",
],
  top_and_bottom_side_pre_heat: [
    "Uniform Dual-Side Heating: Simultaneously heats both the top and bottom surfaces of the PCB for consistent temperature across the entire board.",
    "Improved Solder Flow and Wetting: Ensures better solder joint formation by maintaining optimal temperatures for flux activation and solder flow.",
    "Reduced Thermal Stress: Balanced heating minimizes the risk of thermal shock, warping, or delamination of sensitive components.",
    "Adjustable Heating Profiles: Customizable temperature settings for different board types, sizes, and materials.",
    "Enhanced Process Stability: Promotes repeatable and reliable soldering, reducing the risk of defects such as cold solder joints or poor wetting.",
],
  top_side_pre_heat: [
    "Uniform Component Heating: Ensures even temperature distribution across the top side of the PCB.",
    "Reduces Thermal Shock: Minimizes stress on sensitive components, preventing cracking or delamination.",
    "Enhances Solder Flow and Wetting: Improves joint formation by maintaining optimal temperatures.",
    "Open-Loop Heat Profiles: Adjustable power levels to suit different PCB designs and materials.",
    "Energy-Efficient and Non-Contact: Provides controlled heating without physical contact, reducing wear and tear.",
  ],
  ap_nozzle: [
    "Versatile Design: Capable of handling multiple board types and component configurations, making it ideal for diverse production needs.",
    "Precision Flux and Solder Application: Provides accurate, consistent deposition of flux and solder to ensure high-quality joints.",
    "Adjustable Settings: Customizable airflow, solder volume, and temperature control for optimized performance across different applications.",
    "Quick Changeover: Easily adaptable for different components and board layouts, reducing downtime and increasing productivity.",
    "Durable and Reliable: Built for long-lasting performance, even in high-volume production environments, with minimal maintenance requirements",
  ],
  custom_nozzle: [
    "Tailored to Application Needs: Designed specifically for unique board layouts, component placements, or soldering profiles to achieve optimal results.",
    "Precision Flux and Solder Deposition: Ensures accurate, targeted application of flux and solder, improving joint quality and reducing defects.",
    "Optimized for Efficiency: Customization allows for better use of time and resources, minimizing waste and reducing the need for frequent adjustments.",
    "Enhanced Compatibility: Ensures the nozzle works seamlessly with specific components, reducing the risk of damage or poor-quality joints.",
    "Durable and Long-Lasting: Built for high-performance, the custom nozzle is designed to withstand the demands of continuous soldering operations in specialized environments.",
  ],
  jet_wave: [
    "High-Precision Soldering: The jet of molten solder ensures consistent, accurate coverage of targeted areas, reducing the risk of solder bridging or cold joints.",
    "Improved Process Efficiency: By combining jetting and wave technologies, the Jet-Wave delivers fast, reliable soldering with minimal cycle times, increasing overall throughput.",
    "Uniform Solder Flow: The wave soldering component guarantees a smooth, uniform solder flow, enhancing joint integrity and reducing defects.",
    "Reduced Flux and Solder Waste: The controlled application of flux and solder minimizes excess material usage, making the process more cost-effective and environmentally friendly.",
    "Ideal for Complex Assemblies: Perfect for high-density PCBs and intricate component layouts, the Jet-Wave ensures excellent coverage and joint quality even in challenging applications.",
  ],
  jet_tip_nozzle: [
    "Higher Solder Flow: The higher reach of solder enables the Jet-Tip to reach geometrically cocmplex areas, reducing the need for customized nozzles.",
    "Improved Top-Side Wetting: Due to the increased volume of flow, the Jet-Tip offers better top-side solder penetration compared to traditional AP nozzles.",
    "Consistent Solder Flow: The system ensures smooth, even solder flow across all joints, improving the overall quality of the soldering process.",
    "Reduced Defects: By providing uniform solder coverage, it minimizes the risk of cold joints, bridging, or incomplete soldering.",
    "Versatile Application: Works effectively on a variety of PCB sizes and layouts, making it ideal for diverse industries including consumer electronics, automotive, and telecommunications.",
],
  micro_nozzle: [
    "Precision Soldering: The micro-nozzle delivers highly focused flux and solder, ensuring perfect joint formation on small or sensitive components without overspray.",
    "Patented Technology: Pillarhouse’s patented design ensures maximum accuracy, reliability, and repeatability, making it the ideal choice for complex and intricate soldering tasks.",
    "Reduced Heat Impact: By focusing on the soldering area, the micro-nozzle minimizes thermal exposure to surrounding components, reducing the risk of damage or thermal shock.",
    "Enhanced Process Control: Offers fine-tuned control over solder deposition, improving joint quality, reducing solder bridging, and minimizing excess flux or solder waste.",
    "Compact and Flexible: Its small size and adaptable design allow it to reach tight spots and work with compact, densely populated PCBs.",
],
  wide_wave: [
    "Broad Solder Coverage: The wide wave of solder enables efficient and uniform coverage over large areas, reducing the need for multiple passes.",
    "Improved Process Speed: Ideal for high-volume production, the wide wave increases throughput by covering multiple components in a single pass.",
    "Consistent Solder Flow: The system ensures smooth, even solder flow across all joints, improving the overall quality of the soldering process.",
    "Reduced Defects: By providing uniform solder coverage, it minimizes the risk of cold joints, bridging, or incomplete soldering.",
    "Versatile Application: Works effectively on a variety of PCB sizes and layouts, making it ideal for diverse industries including consumer electronics, automotive, and telecommunications.",
],
  control: [
    "Graphical Programming Interface: Fast and intuitive point mapping using visual aids and layered data.",
    "Efficient Workflow Tools: Group joints into reusable local or global packages; use copy/paste tools to increase programming speed.",
    "Advanced MES Integration: Control user access, track and parse program changes, and upload directly to external monitoring systems.",
    "Smart Predictive Diagnostics: Monitors historical performance trends to detect and alert on potential failures (e.g., nozzle wear, fluid level anomalies).",
    "High-Level Process Control: Enables centralized and secure control over critical process parameters with real-time feedback.",
  ],
  software: [
    {
      name: "Interface",
      text: "The Graphical User Interface of PillarCOMM.NET has been developed to follow the principles of simplicity and informativeness. The application’s main window displays each cell within the machine, along with easily identifiable icons, to represent each component fitted to the machine.\n\nThis design allows for ease of use, accessibility and a reduction in the learning curve required to operate Pillarhouse International machinery.\n\nEvery option fitted to the machine can be configured using the build menu to specify what the machine entails, making the addition of options and customizing the machine for customers’ specific needs easier, all while maintaining a ubiquitous code base.",
    },
    {
      name: "Additional Features",
      text: [
        "Security user login facility, allowing the customer to protect programs and machine settings",
        "Try action functionality, allowing the customer to enhance and customise programs by selecting and running specific joints from within the program",
        "Process logs are generated automatically, allowing customers to view information about their machine productivity and efficiency",
        "Change log and downtime history viewer within the application, allowing the customer to understand and see where further process optimisation can be undertaken",
        "Precise fiducial correction which comes with an auto re-detect system if the fiducial is not found the first time. On fiducial re-detect, there are tools available to accurately select the correct fiducial point",
        "Real time language switching functionality with multiple languages available",
        "HD camera viewing available on multiple screens",
        "Auto-sleep functionality, with settable wakeup schedules so the machine is ready for operators as soon as they want to run product through the machine. This includes a nozzle soak time, to ensure optimal nozzle temperature before the cycle begins",
        "Offline programming facility identical to the programming editing facilities within the main application, ensuring no additional learning curve is required. Furthermore, step and repeat is available, so that pallets containing multiple of the same PCB can be processed efficiently. Our editing program facility was designed largely alongside senior process engineers with years of experience, providing the capabilities required for the most complex of selective needs.",
        "Configurable RGB lighting available for different machine states.",
        "Programs from previous machines can be loaded into the Fusion and Synchrodex Pro.",
      ].map((item) => `• ${item}`).join("\n\n"),
    },
    {
      name: "Machine Logging",
      text: "PillarCOMM.NET comes with multiple logging and error detection techniques. While the machines are turned on, logs are generated in order to provide information to Pillarhouse International engineers in order to optimise and or provide help with any issue the customer may be experiencing. The way the software has been designed allows engineers to log in remotely and use a number of debugging techniques to help customers debug and fix their machines if any problems occur. Moreover, multiple notification systems have been built into the software to provide clear error reporting to the user and provide notifications on the best route to take while operating the machine. Warnings are sent in order to help users follow the correct procedures to get the most out of operating times.",
    },
    {
      name: "Variations",
      text: (
        <>
          Different variations of PillarCOMM are required for different machine types, with the corresponding machines for each PillarCOMM variation listed below:
          {SOFTWARE_VARIATIONS.map(({ variant, machines }) => (
            <Fragment key={variant}>
              {"\n\n"}
              <strong>{variant}</strong>:{" "}
              {machines.map(({ name, url }, idx) => (
                <Fragment key={name}>
                  {idx > 0 && ", "}
                  <a href={url} target="_blank" rel="noopener noreferrer">{name}</a>
                </Fragment>
              ))}
            </Fragment>
          ))}
        </>
      ),
    },
  ],
  default: [
    "This compact modular in-line system ulitises the same design concept as the new generation Synchrodex Pro soldering cell and offers the user significantly reduced process time when compared to using a single soldering unit with on-board fluxing and preheat functions.",
    "The fluxer/preheat combo operates in tandem with the selective soldering unit and can be configured with either Drop-Jet or ultrasonic flux heads and both top and bottom side IR preheat functions. Twin pitch fluxheads are available for further cycle time reductions.",
    "The machine utilizes an XY positioning system to position the flux head as required. A Drop-Jet flux head is supplied as standard with an ultrasonic head being ordered as an alternative or in addition to the standard head. Both systems provide precise control of the flux being deposited on the board.",
    "Programming and control of the machine is via a PC using the Windows® based new generation PillarCOMM software. Programs and machine configuration are protected by a customer selectable, multilevel password security system.",
    "The machine is freely programmable to cater to different board layouts. Each flux joint is separately programmable to its own set of required parameters. The programming system employs a component data library enabling quantities of similar joints to use the same fluxing data outline. Visually assisted self-teach alignment allows for fast and accurate board programming.",
  ],
  "Orissa Synchrodex": [
    "Designed as a modular system, the Synchrodex range of in-line equipment offers the ultimate in flexibility with the ability to upgrade to high speed throughput when multiple modules are placed together.",
    "Supplied as standard with our patented design Drop-Jet fluxer, this system offers accurate, controlled flux deposition either prior to or during the optional top side preheat function. Preheat can be controlled via an optional top-side closed loop pyrometer system for optimum temperature profile regulation.",
    "Our low maintenance solder bath and pump mechanism moves in three axes. Solder is applied using our proven technology single point AP nozzle design incorporating patented spiral solder return to bath technology offering increased wave stability with reduced potential for solder balls. The system can also accommodate our latest generation micro nozzle, together with Jet-Wave, Wide-Wave and dedicated multi-tube single dip solder technology.",
    "As with all Pillarhouse systems, the soldering process is enhanced by a hot Nitrogen curtain which provides an inert atmosphere for the soldering process and also assists in the prevention of oxidation. This process provides a local preheat to the joint thus reducing thermal shock to localised components.",
    "The Synchrodex is controlled by a PC, through PillarCOMM, a Windows® based ‘Point & Click’ interface with PCB image display. Additionally, our optional PillarPAD offline package allows the operator to produce programs independently from the machine using Gerber data.",
  ],
  "Orissa Synchrodex Pro": [
    "Designed as a modular system, the Synchrodex Pro range of in-line equipment offers the ultimate in flexibility with the ability to upgrade to high speed throughput when multiple modules are placed together.",
    "Supplied as standard with our patented design Drop-Jet fluxer, this system offers accurate, controlled flux deposition either prior to or during the optional top side preheat function. Preheat can be controlled via an optional top-side closed loop pyrometer system for optimum temperature profile regulation.",
    "Our low maintenance solder bath and pump mechanism moves in three axes. Solder is applied using our proven technology single point AP nozzle design incorporating patented spiral solder return to bath technology offering increased wave stability with reduced potential for solder balls. The system can also accommodate our latest generation micro nozzle, together with Jet-Wave, Wide-Wave and dedicated multi-tube single dip solder technology.",
    "As with all Pillarhouse systems, the soldering process is enhanced by a hot Nitrogen curtain which provides an inert atmosphere for the soldering process and also assists in the prevention of oxidation. This process provides a local preheat to the joint thus reducing thermal shock to localised components.",
    "The Synchrodex Pro is controlled by a PC, through new generation PillarCOMM, a Windows® based ‘Point & Click’ interface with PCB image display. Additionally, our optional PillarPAD offline package allows the operator to produce programs independently from the machine using Gerber data.",
  ],
  "Fusion Layouts": [
    "Incorporating high speed PCB transfer, the Orissa Fusion platform offers the ultimate in flexibility, coupled with reduced line length at a lower cost compared to current market offerings.",
    "The standard four station cell can be configured to handle PCB’s up to 381mm x 460mm - fluxer, preheat, solder, solder. For high speed applications this same unit can be configured as fluxer/preheat and up to three solder modules with as many as five heater options.",
  ],
};

export const SYSTEM_OPTIONS = {
  default: [
    "Top side IR preheat, with closed-loop control",
    "Motorised underside IR preheat, at all cell positions",
    "Ultrasonic fluxing",
    "Dual Drop-Jet / Ultrasonic fluxing",
  ],
  "Orissa Synchrodex": [
    "Ultrasonic fluxing",
    "Dual Drop-Jet / ultrasonic fluxing",
    "Top-side instant IR preheat",
    "Closed loop pyrometer temperature control",
    "Bottom-side hot Nitrogen selective preheat",
    "Laser PCB warp correction",
    "Solder reel identification",
    "Solder bath coding - identifies correct bath for program",
    "Encoders on X, Y and Z axis",
    "Nitrogen generator",
  ],
  "Orissa Synchrodex Pro": [
    "High definition process viewing camera",
    "Top side IR preheat, with closed-loop control",
    "Motorised underside IR preheat, at all cell positions",
    "Ultrasonic fluxing",
    "Dual Drop-Jet / Ultrasonic fluxing",
    "Laser-based PCB warp correction",
    "Solder reel identification",
    "Nitrogen generator",
  ],
  "Fusion Layouts": [
    "Ultrasonic fluxing",
    "Dual Drop-Jet / ultrasonic fluxing",
    "Top-side instant IR preheat",
    "Bottom-side IR preheat",
    "Closed loop pyrometer temperature control",
    "Large solder bath for dedicated single dip applications",
    "Laser PCB warp correction",
    "1.5mm micro nozzle",
    "Solder reel identification",
    "Auto-nozzle conditioning system",
    "Larger PCB handling size",
    "Nitrogen generator",
  ],
};

export const MONITORING_OPTIONS = {
  default: [
    "Flux spray monitoring",
    "Flux flow monitoring",
    "Flux spray & flow monitoring",
  ],
  "Orissa Synchrodex": [
    "Flux presence sensor - thermistor style",
    "Flux spray, flow and spray & flow",
    "O2 ppm",
    "Nitrogen flow",
  ],
  "Orissa Synchrodex Pro": [
    "Flux spray, flow and spray & flow",
    "Pump rpm",
    "O2 ppm",
    "Nitrogen flow",
  ],
  "Fusion Layouts": [
    "Flux presence sensor - thermistor style",
    "Flux spray, flow and spray & flow",
    "O2 ppm",
    "Nitrogen flow",
  ],
};

export const TECHNOLOGY_SLIDES = {
  default: [
    {
      type: "image",
      description:
        "This compact modular in-line system ulitises the same design concept as the new generation Synchrodex Pro soldering cell and offers the user significantly reduced process time when compared to using a single soldering unit with on-board fluxing and preheat functions.",
      content: Fusion4Product,
    },
    {
      type: "image",
      description:
        "The fluxer/preheat combo operates in tandem with the selective soldering unit and can be configured with either Drop-Jet or ultrasonic flux heads and both top and bottom side IR preheat functions. Twin pitch fluxheads are available for further cycle time reductions.",
      content: JadeS200MK2Product,
    },
    {
      type: "image",
      description:
        "This compact modular in-line system ulitises the same design concept as the new generation Synchrodex Pro soldering cell and offers the user significantly reduced process time when compared to using a single soldering unit with on-board fluxing and preheat functions.",
      content: Fusion4Product,
    },
    {
      type: "image",
      description:
        "The fluxer/preheat combo operates in tandem with the selective soldering unit and can be configured with either Drop-Jet or ultrasonic flux heads and both top and bottom side IR preheat functions. Twin pitch fluxheads are available for further cycle time reductions.",
      content: JadeS200MK2Product,
    },
  ],
  fluxing: fluxingSliders,
  auto_purge: fluxingSliders,
  djf: fluxingSliders,
  dual_fluxer: fluxingSliders,
  ofps: fluxingSliders,
  usf: fluxingSliders,
  thermistor_flux_sensing: fluxingSliders,
  preheating: preheatingSliders,
  bottom_side_pre_heat: preheatingSliders,
  closed_loop_bottom_side_pyro_control: preheatingSliders,
  closed_loop_top_side_pyro_control: preheatingSliders,
  convection_pre_heat: preheatingSliders,
  ring_heater: preheatingSliders,
  top_and_bottom_side_pre_heat: preheatingSliders,
  top_side_pre_heat: preheatingSliders,
  soldering: solderingSliders,
  ap_nozzle: solderingSliders,
  custom_nozzle: solderingSliders,
  jet_wave: solderingSliders,
  jet_tip_nozzle: solderingSliders,
  micro_nozzle: solderingSliders,
  wide_wave: solderingSliders,
  control: [
    {
      type: "video",
      name: "Control Software",
      option: "control_software",
      description: "",
      content: controlVideo,
      enhancements: true,
    },
  ],
  other: [
    {
      type: "image",
      name: "Fiducial Dynamic Correction",
      option: "fiducial_dynamic_correction",
      icon: IconFiducialDynamicCorrection,
      description:
        "Achieve unparalleled precision with Automatic Fiducial Correction. This advanced feature corrects XY and theta axes with high accuracy, using any unique and accurately positioned mark—no specific fiducial marks required.",
      content: `${IMAGE_BASE_URL}enhancements/Fiducial.jpg`,
      enhancements: true,
    },
    {
      type: "video",
      name: "PCB Warpage Correction",
      option: "pcb_warpage_correction",
      icon: IconPcbWarpageCorrection,
      description:
        "Ensure flawless soldering with PCB Active Warpage Correction!.Our system checks PCB height at programmed locations before soldering and adjusts Z height as needed, preventing skips and PCB contact.",
      content: `${VIDEO_BASE_URL}enhancements/boardwarp.mp4`,
      enhancements: true,
    },
    {
      type: "image",
      name: "Electromagnetic Pump",
      option: "electromagnetic_pump",
      icon: IconElectromagneticPumpFlow,
      description:
        "Discover the superior efficiency of our Electromagnetic Pump, featuring Pillarhouse design for enhanced thermal continuity and reduced pumping variation.",
      content: `${IMAGE_BASE_URL}enhancements/mag pump.jpg`,
      enhancements: true,
    },
    {
      type: "video",
      name: "Dual Solder Baths",
      option: "dual_solder_baths",
      icon: IconDualSolderBaths,
      description:
        "Maximize efficiency with our Dual Bath system. Whether dual bath on XY or dual XY configurations, customize to your requirements for high flexibility, high throughput, or both.",
      content: `${VIDEO_BASE_URL}enhancements/TwinBath.mp4`,
      enhancements: true,
    },
    {
      type: "image",
      name: "MES (Manufacturing Equipment Systems)",
      option: "manufacturing_equipment_systems",
      icon: IconMes,
      description:
        "Unlock the power of MES with Pillarhouse. From basic data transfer to advanced database parsing and full Factory 4.0 data tracking, our customizable solutions fit every level of complexity and budget. As pioneers in Factory 4.0 software development, we lead the way in modern manufacturing integration.",
      content: `${IMAGE_BASE_URL}enhancements/MES.jpg`,
      enhancements: true,
    },
    {
      type: "image",
      name: "Automatic Rework Integration",
      option: "automatic_rework_integration",
      icon: IconAutomaticRework,
      description:
        "Streamline corrections with Pillarhouse's Automatic Rework Integration! Our open architecture interfaces with any AOI brand, automatically programming rework points following wave or other processes. A manual version with a workstation is also available, both require the barcode program change option.",
      content: `${IMAGE_BASE_URL}enhancements/AOI-Automatic.png`,
      enhancements: true,
    },
    {
      type: null,
      name: "Internal Monitoring of Machine Temperature",
      option: "internal_monitoring_temperature",
      icon: IconMachineTemperature,
      description:
        "Optimize performance with Internal Monitoring of Machine Temperature. Unlike hot air systems, it controls ambient overload from constant high thermal demand by detecting temperature changes and compensating at the board level.",
      content: null,
      enhancements: true,
    },
    {
      type: "image",
      name: "Automatic Program ID and Change",
      option: "automatic_program_id",
      icon: IconBarcode,
      description:
        "Streamline your operations with our Barcode Reader integration. Whether handheld or mounted, it ensures correct program loading every time. Upgrade with full MES and FIS PCB tracking by serial number for enhanced efficiency and traceability.",
      content: `${IMAGE_BASE_URL}enhancements/Bar code reader.png`,
    },
  ],
};

export const OTHER_CHOICES = [
  {
    name: "Pilot",
    forwardTo: "/product?name=pilot",
    description:
      "Entry-level, benchtop, single point selective soldering system",
    content: Pilot,
  },
  {
    name: "JADE MKII",
    forwardTo: "/product?name=Jade MKII",
    description:
      "Elementary, flexible, single point selective soldering system",
    content: JadeMK2,
  },
  {
    name: "QUADRON (PLC)",
    forwardTo: "/product?name=Quadron",
    description: "High specification, PLC, rotary soldering machine",
    content: QuadronPLC,
  },
  {
    name: "JADE MKIV",
    forwardTo: "/product?name=Jade MKIV",
    description:
      "Entry-level, benchtop, single point selective soldering system",
    content: JadeMK4,
  },
  {
    name: "JADE PRO",
    forwardTo: "/product?name=Jade Pro",
    description:
      "Enhanced, entry-level, single point selective soldering system",
    content: JadePro,
  },
  {
    name: "JADE PRODEX",
    forwardTo: "/product?name=JADE Prodex",
    description:
      "Ultra-flexible, offline, multi-platform, quick load twin PCB rotary table selective soldering system",
    content: JadeProDex,
  },
];

export const INTRO_TEXT = {
  fluxing: `Welcome to the Pillarhouse Flux Application Page \n\n
  Pillarhouse provides a comprehensive range of flux application solutions tailored to customer needs, including throughput, flux selection, and application methods. Our advanced sensing systems offer thermal and optical detection, enabling precise estimation or measurement of flux deposition in micrograms. These capabilities integrate seamlessly with our IPC 2591 and MES systems, ensuring high precision and reliability in flux application.`,
  preheating: `Welcome to the Pillarhouse Preheat Technology Page \n\n
Pillarhouse offers the most advanced and comprehensive preheat solutions in the selective soldering market. With decades of experience and a commitment to focused research, our technology provides precision heating options, including top and bottom adjustable zones, IR with multiple wavelengths, closed-loop control, and full convection heating. Our high-speed convection stacker and specialized solutions, such as the bottom ring heater for embedded PCBs in EV modules, set us apart. We excel in meeting the most challenging high-thermal energy requirements, particularly in military, space and automotive applications.`,
  soldering: `Welcome to the Pillarhouse Soldering Technology Page \n\n
Our award-winning and patented soldering technology delivers unparalleled precision and capability. Pillarhouse offers a broad range of nozzle solutions, from standard wetted quick-change nozzles (2.5mm to 16mm) to extended-length versions, as well as high-flow Jet_Tip nozzles (6mm to 45mm). Our industry-leading Micro Thermal 1.5mm Nozzle provides the best keep-out zones while delivering exceptional thermal energy—far superior to standard designs twice its size.\n

Unique to Pillarhouse, our JetWave technology combines high flow rates with tight control, delivering best-in-class thermal performance with nozzles that last for years and require minimal maintenance. Most of our nozzles are available in either screw-in quick-change tips or magnetic tips (Micro nozzles available only in quick-change mechanical tips).`,
  auto_purge: "The Auto-Purge Option is an advanced feature designed to maintain optimal performance and reliability in selective soldering systems by preventing flux buildup and clogging. This automated system periodically flushes the fluxing mechanism, ensuring consistent spray patterns, reducing maintenance downtime, and extending equipment lifespan.\n\nIdeal for high-reliability manufacturing environments, the auto purge fluxing option enhances process stability, reduces operational costs, and ensures peak fluxing performance.",
  djf: "Pillarhouse’s Patented Drop Jet Flux Head is a precision-engineered component designed for ensuring accurate and controlled flux deposition. Utilizing a non-contact spray system, this advanced fluxing technology delivers precise, repeatable flux application to targeted areas of the PCB without overspray or contamination.\n\nIdeal for through-hole and selective soldering processes, a drop jet flux head enhances solderability, improves process repeatability, and supports high-reliability electronic assemblies.",
  dual_fluxer: "The Dual Fluxer is an advanced flux application system that offers the flexibility to apply two different flux types within a single process. This capability allows manufacturers to optimize soldering performance for various board designs, flux chemistries, or specific application requirements without manual changeovers.\n\nIdeal for complex PCB assemblies with varying flux requirements, the dual fluxer enhances selective soldering performance by delivering precise, efficient, and reliable flux application tailored to specific process needs.\n",
  ofps: (
    <>
      <strong>Optical Flux Presence Sensor (OFPS)</strong>
      {"\n\n"}
      The Optical Flux Presence Sensor uses a light sensor mounted opposite an LED light source. A sponge pad is mounted above the Flux Plume Sensor assembly to absorb the flux spray during flux sensing operation.
      {"\n\n"}
      The flux head is positioned below and between the two sensors and the signal is first measured before the flux is fired. This measurement is held in a “sample and hold circuit”, while a second measurement is taken during flux firing.
      {"\n\n"}
      The two readings are then compared, and the difference is used as a measurement of the Flux firing. Threshold levels can be set to provide an error message when the Flux firing Reading is out of the threshold range.
    </>
  ),
  thermistor_flux_sensing: "The Thermistor Flux Sensing system is an advanced monitoring solution designed to ensure accurate and consistent flux application during selective soldering. By utilizing temperature-sensitive thermistors, this system detects the presence and flow of flux, helping to maintain optimal process conditions and prevent defects caused by insufficient or excessive flux.\n\nIdeal for high-reliability manufacturing environments, thermistor flux sensing enhances process control, improves soldering consistency, and reduces rework by ensuring proper flux application at all times.",
  usf: "The Ultrasonic Fluxing system is an advanced flux application technology that uses high-frequency ultrasonic vibrations to create a fine, uniform mist of flux. This method ensures precise, controlled deposition with minimal overspray, improving soldering consistency and reducing flux waste.\n\nIdeal for high-reliability applications in industries such as automotive, aerospace, and electronics manufacturing, ultrasonic fluxing enhances selective soldering performance by providing superior flux control and application precision.",
  bottom_side_pre_heat: "The Bottom-Side Infrared (IR) Pre-Heating system is a crucial component in selective soldering, designed to preheat printed circuit boards (PCBs) from below. By using infrared radiation, this system ensures uniform and controlled heating, improving solder flow, reducing thermal stress, and enhancing overall soldering quality.\n\nIdeal for complex assemblies and high-reliability applications, bottom-side IR pre-heating optimizes the selective soldering process by ensuring stable temperatures, reducing defects, and improving overall soldering performance.",
  closed_loop_bottom_side_pyro_control: "The Bottom-Side Closed-Loop Pyrometer is designed to precisely measure and control the bottom-side pre-heating temperature of printed circuit boards (PCBs) during selective soldering. By using infrared (IR) sensing technology, this system ensures accurate, real-time temperature feedback, optimizing heating performance and preventing thermal-related defects.\n\nIdeal for high-reliability applications, the closed-loop bottom-side pyrometer enhances selective soldering by delivering consistent pre-heating, reducing rework, and improving overall soldering quality.",
  closed_loop_top_side_pyro_control: "The Closed-Loop Top-Side Pyrometer is an advanced temperature monitoring system designed to precisely measure and control the top side pre-heating temperature of printed circuit boards (PCBs) during selective soldering. By using infrared (IR) sensing technology, this system ensures accurate, real-time temperature feedback, optimizing heating performance and preventing thermal-related defects.\n\nIdeal for high-reliability applications, the closed-loop top side pyrometer enhances selective soldering by delivering consistent pre-heating, reducing rework, and improving overall soldering quality.",
  convection_pre_heat: "The Convection Pre-Heat system is a highly efficient method for uniformly heating printed circuit boards (PCBs) before selective soldering. Using forced hot air circulation, this system ensures even heat distribution, reducing thermal stress, improving solder flow, and enhancing overall process stability.\n\nIdeal for high-reliability applications, convection pre-heating optimizes the selective soldering process by ensuring consistent temperatures, reducing defects, and improving overall soldering quality.",
  ring_heater: "The IR Ring Heater is a specialized infrared heating solution designed for precise, focused pre-heating of specific areas on printed circuit boards (PCBs) during selective soldering. This system utilizes a circular infrared heating element, which delivers uniform and controlled heat to the target area, ensuring optimal temperature for soldering without affecting surrounding components.\n\nIdeal for selective soldering of high-precision components, the IR ring heater enhances soldering accuracy, improves process reliability, and ensures high-quality solder joints by providing optimal pre-heating conditions.",
  top_and_bottom_side_pre_heat: "The Top and Bottom Side Pre-Heat system is an integrated solution designed to uniformly heat both the top and bottom surfaces of printed circuit boards (PCBs) before selective soldering. This dual-side heating approach ensures that the PCB reaches an optimal temperature, reducing thermal shock, improving solder flow, and enhancing overall process reliability.\n\nIdeal for high-reliability applications, the top and bottom side pre-heat system improves the selective soldering process by providing even and controlled heating, ensuring high-quality solder joints and reducing rework or defects.",
  top_side_pre_heat: "The Top-Side Infrared (IR) Pre-Heating system is designed to preheat printed circuit boards (PCBs) from above during the selective soldering process. By gently warming the components and board surface, this system helps reduce thermal shock, improve solder flow, and enhance overall process reliability.",
  ap_nozzle: "The All-Purpose Nozzle is a versatile and adaptable component designed to accommodate a wide range of soldering applications in selective soldering systems. Engineered for flexibility, this nozzle is capable of handling various board sizes, component types, and soldering profiles, making it an essential tool for high-efficiency, high-quality production.\n\nPerfect for industries requiring flexibility and efficiency, the all-purpose nozzle enhances selective soldering operations by offering consistent, reliable performance across a broad range of applications, ensuring high-quality solder joints while maximizing process efficiency.",
  custom_nozzle: "The Custom Nozzle is a tailored solution designed to meet the specific needs of unique soldering applications. By providing a nozzle that is precisely engineered to fit a particular PCB layout, component configuration, or process requirement, this solution offers enhanced performance, accuracy, and efficiency in selective soldering.\n\nIdeal for complex or high-precision applications, the custom nozzle enhances selective soldering by delivering tailored, consistent, and high-quality results, making it an essential tool for achieving excellent solder joints in unique production scenarios.",
  jet_wave: "The Jet-Wave is a state-of-the-art fluxing and soldering solution that combines the benefits of both jetting and wave soldering technologies. This advanced system uses a high-velocity, controlled stream of molten solder, providing exceptional precision and coverage for selective soldering applications. The Jet-Wave system is designed to optimize soldering quality, improve throughput, and reduce defects, making it ideal for high-precision electronics manufacturing.\n\nIdeal for industries that require precise, high-quality solder joints, the Jet-Wave system combines the best aspects of jetting and wave soldering technologies, offering improved soldering performance, efficiency, and reduced rework.",
  jet_tip_nozzle: "Jet_Tip Nozzle – Bridges the gap between AP-1 and JetWave, providing higher flow than AP-1 with full control and enhanced heat transfer. While it sacrifices some debridging performance, it offers flexibility by allowing two different contact sizes in the same nozzle.",
  micro_nozzle: "The Pillarhouse Patented Micro-Nozzle is a cutting-edge, precision-engineered nozzle designed for selective soldering applications that demand the utmost accuracy and control. This innovative nozzle is specifically crafted to deliver a finely focused and consistent stream of flux and solder, ideal for small, high-precision components and densely packed PCB designs.\n\nPerfect for industries such as automotive, aerospace, and telecommunications, the Pillarhouse Patented Micro-Nozzle ensures superior soldering precision, enhanced joint reliability, and reduced rework, making it an indispensable tool for selective soldering processes that require pinpoint accuracy.",
  wide_wave: "The Wide-Wave is an advanced selective soldering solution designed to provide broader and more uniform solder coverage for large or complex printed circuit boards (PCBs). By utilizing a wide wave of molten solder, this system ensures consistent solder application across a larger area, making it ideal for boards with multiple components or wide-pad layouts.\n\nPerfect for applications that require efficient, high-quality soldering across large or complex PCBs, the Wide Wave system enhances selective soldering performance by delivering consistent, high-speed solder application and reducing rework or defects.",
  control: "Welcome to Pillarhouse PillarCOMM.NET\n\n PillarCOMM.NET Software Control Package: Smarter, Faster, More Precise Process Control\n\n PillarCOMM.NET is Pillarhouse’s advanced software control package, designed to streamline the programming and operation of selective soldering systems. With an intuitive graphical interface, users can quickly and accurately map solder points using a combination of onboard camera imagery, Gerber files, and PCB images. This integrated visual environment significantly accelerates setup and programming.\n\n Advanced features such as grouped joint control, local and global package creation, and point-pasting tools dramatically reduce programming time while enhancing consistency across jobs.\n At the management level, PillarCOMM.NET provides granular MES integration, enabling detailed user access control, program change tracking, and direct upload to monitoring systems. Built-in smart diagnostics leverage historical data to anticipate and alert users to potential issues—such as nozzle wear and level control problems—before they impact production.\n With real-time monitoring, lifetime tracking, and predictive alerts, PillarCOMM.NET delivers unmatched precision and confidence in process control. Whether you're optimizing for speed, traceability, or quality assurance, PillarCOMM.NET gives you complete control at every level.",
  software: (
    <>
      <strong>Intelligent, User-Friendly Machine Software</strong>
      {"\n\nPillarhouse machine software is designed to make programming, operation, and process monitoring simple and intuitive. The graphical interface provides operators with a clear view of each machine cell and its installed components, reducing training time while improving accessibility and ease of use.\n\nAdvanced features include secure user access, offline programming, automatic process logging, downtime tracking, precise fiducial correction, real-time language switching, remote diagnostics, and configurable machine-status lighting. Programs can be created, optimized, and transferred efficiently, while detailed notifications and diagnostic tools help operators maximize productivity and minimize downtime.\n\nWith software platforms tailored to each Pillarhouse machine, customers receive a flexible and scalable solution designed to support both current production requirements and future expansion."}
    </>
  ),
  offline: "Welcome to Pillarhouse PillarCOMM.NET OFFLINE\n\n PillarCOM.NET OFFLINE is Pillarhouse’s advanced software control package, purpose-built to streamline the programming and operation of selective soldering systems without tying up production machines. Using a highly intuitive graphical interface, users can program complete jobs fully offline. Gerber file integration, onboard camera imagery, and PCB images work together in a single visual environment, making it easy to identify thermal indicators and define solder points quickly and accurately.\n\n The offline platform delivers unmatched flexibility and efficiency. Engineers can prepare, review, and optimize programs using the same advanced tools as the online version—entirely independent of machine time. Features like grouped joint control, library creation, and point-pasting tools reduce setup time while ensuring consistency across jobs. With PillarCOM.NET OFFLINE, all pertinent process data is preserved, programs are uploaded directly to the machine with no conversion required, and valuable production downtime is avoided.\n\n At the management level, PillarCOM.NET OFFLINE provides complete process oversight from a remote station. User access control, program change tracking, and direct upload to monitoring systems are all built-in. Historical data is leveraged through smart diagnostics, offering predictive alerts that flag potential issues such as nozzle wear and level control problems before they impact throughput. Whether you need speed, traceability, or quality assurance, PillarCOM.NET OFFLINE gives you unmatched control at every",
  other: (
    <>
      <strong>Technologies Tailored to Your Process</strong>
      {"\n\n"}
      Every manufacturing environment is different, which is why Pillarhouse offers a wide range of additional technologies and machine options designed to tailor each system to the customer’s specific process, production requirements, and level of automation.
      {"\n\n"}
      Whether supporting a smaller manufacturer looking for greater process control or a high-volume operation requiring advanced automation, traceability, and factory integration, Pillarhouse systems can be configured to suit virtually any application or industry.
      {"\n\n"}
      Available technologies include{" "}
      <strong>Fiducial Dynamic Correction, PCB Warpage Correction, Electromagnetic Pump Technology, Dual Solder Baths, MES Integration, Automatic Rework Integration, Internal Machine Temperature Monitoring, and Automatic Program Identification and Changeover</strong>
      , among many others.
      {"\n\n"}
      From standalone process enhancements to fully integrated production solutions, our goal is to provide the right level of technology for each customer—
      <strong>regardless of company size, production volume, or industry.</strong>
    </>
  ),
};
